/**
 * Rolling 24h DP-optimering — matchar Nordpool Day-Ahead-marknaden.
 *
 * Nordpool publicerar nästa dygns spotpriser kl 13:00 CET/CEST. Vid den
 * tidpunkten har ett verkligt batterisystem följande framförhållning:
 *   - 14:00 idag → 23:00 idag = 10 timmar (redan kända från igår 13:00)
 *   - 00:00 imorgon → 23:00 imorgon = 24 nya timmar (publicerades just nu)
 *
 * Total framförhållning vid 13:00: 34 timmar. Sedan rullar fönstret tills
 * nästa 13:00.
 *
 * Algoritmen:
 *   1. Hitta alla 13:00-timmar i datat (lokal tid)
 *   2. Vid varje 13:00, kör DP-optimering över de 34 timmar som då är kända
 *   3. Använd de besluten tills nästa 13:00 publicerar ny info
 *   4. Mellan publiceringar är besluten "låsta" — vi vet inte mer än vi gör
 *
 * Skillnaden mot optimal-foresight: vi får inte se framtid längre än 34
 * timmar bort. Skillnaden mot heuristik: vi använder DP istället för
 * sortering, vilket fångar timing-strukturen (t.ex. att spara kapacitet
 * till morgondagens dyrare topp istället för att tömma på idag-kvällens
 * mediokra topp).
 */

import type {
  BatteryConfig,
  HourPoint,
} from "./types";
import { solveOptimalPolicy, type DPPolicy } from "./optimal-dp";

/**
 * Tidpunkt på dygnet (lokal tid 0-23) när Nordpool publicerar nästa dygn.
 */
const PUBLICATION_HOUR_LOCAL = 13;

/**
 * Hur många timmar framåt en publicering täcker (resten av idag + hela
 * morgondagen). Vid 13:00 idag täcks: 14:00-23:00 idag (10h) + hela
 * imorgon (24h) = 34 timmar.
 */
const WINDOW_HOURS_AFTER_PUBLICATION = 34;

/**
 * Extrahera lokal timme (0-23) från en HourPoints ISO-tidsstämpel.
 * Vi använder strängformat (YYYY-MM-DDTHH:00:00±HH:MM) istället för
 * Date.getHours() som beror på körningsmiljöns tidszon.
 */
function localHourFromISO(iso: string): number {
  return parseInt(iso.substring(11, 13), 10);
}

export function precomputeRolling24hPolicy(
  hours: HourPoint[],
  battery: BatteryConfig,
  effectiveImportCost: (h: HourPoint) => number,
  effectiveExportRevenue: (h: HourPoint) => number,
  options?: { socLevels?: number; startSocFraction?: number },
): DPPolicy {
  const totalT = hours.length;
  if (totalT === 0) return { acFlow: [], socTrace: [] };

  const startSocFraction = options?.startSocFraction ?? 0.5;
  const acFlow = new Array(totalT).fill(0);
  const socTrace = new Array(totalT).fill(0);

  let currentSocKWh = battery.capacityKWh * startSocFraction;

  // Hitta alla 13:00-timmar
  const decisionPoints: number[] = [];
  for (let i = 0; i < totalT; i++) {
    if (localHourFromISO(hours[i]!.from) === PUBLICATION_HOUR_LOCAL) {
      decisionPoints.push(i);
    }
  }

  // Hjälpfunktion: kör DP över ett fönster och kopiera resultatet till
  // policy-arrayerna. Returnerar SoC vid slutet av användbar period.
  function processWindow(
    windowStart: number,
    windowEnd: number,
    useUntil: number,
    initialSoc: number,
  ): number {
    if (windowStart >= totalT) return initialSoc;
    const window = hours.slice(windowStart, Math.min(windowEnd, totalT));
    if (window.length === 0) return initialSoc;

    const policy = solveOptimalPolicy(
      window,
      battery,
      effectiveImportCost,
      effectiveExportRevenue,
      { startSocKWh: initialSoc, ...(options?.socLevels && { socLevels: options.socLevels }) },
    );

    const effectiveUseUntil = Math.min(useUntil, totalT, windowStart + policy.acFlow.length);
    let newSoc = initialSoc;
    for (let t = windowStart; t < effectiveUseUntil; t++) {
      const idx = t - windowStart;
      acFlow[t] = policy.acFlow[idx] ?? 0;
      socTrace[t] = policy.socTrace[idx] ?? newSoc;
      newSoc = policy.socTrace[idx] ?? newSoc;
    }
    return newSoc;
  }

  // 1. Initial fönster (innan första 13:00) — vi har ingen perfekt
  //    historik här. Behandla första delen som om en publicering precis
  //    skett vid t=0 (suboptimalt men marginellt — handlar om max en dag).
  const firstDecisionPoint = decisionPoints[0];
  if (firstDecisionPoint === undefined) {
    // Mindre än ett dygn data — kör hela perioden som ett fönster
    currentSocKWh = processWindow(0, totalT, totalT, currentSocKWh);
  } else if (firstDecisionPoint > 0) {
    // Använd första-dagens fönster: från t=0 till första 13:00 + 24h
    const initWindowEnd = Math.min(
      firstDecisionPoint + 1 + WINDOW_HOURS_AFTER_PUBLICATION,
      totalT,
    );
    // Använd planen tills första 13:00 (där nästa publicering tar över)
    const useUntil = Math.min(firstDecisionPoint + 1, totalT);
    currentSocKWh = processWindow(0, initWindowEnd, useUntil, currentSocKWh);
  }

  // 2. För varje 13:00, kör DP över [decisionPoint+1, decisionPoint+1+34]
  //    Använd planen tills NÄSTA 13:00 + 1 (då tar nästa publicering över)
  for (let i = 0; i < decisionPoints.length; i++) {
    const dp = decisionPoints[i]!;
    const windowStart = dp + 1;
    const windowEnd = windowStart + WINDOW_HOURS_AFTER_PUBLICATION;
    const nextDp = decisionPoints[i + 1];
    const useUntil =
      nextDp !== undefined ? nextDp + 1 : Math.min(windowEnd, totalT);

    currentSocKWh = processWindow(windowStart, windowEnd, useUntil, currentSocKWh);
  }

  return { acFlow, socTrace };
}
